import { useMemo } from 'react';
import { Banner, Card, InlineStack, Link, SkeletonBodyText, Text } from '@shopify/polaris';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatShortDate } from '../deep/analyticsHelpers';
import { BalanceChart, EventChips, buildBalanceChartData, buildExpectedSeries, buildIntercompanySeries, collectChartEvents, eur, summarizeHorizon } from '../deep/cashPlanningShared';

// the dashboard shows the next 90 days, without the two least certain layers (order backlog and open purchase orders)
export const DASHBOARD_HORIZON = 90;
export const DASHBOARD_LAYERS = { sales_orders: false, purchase_orders: false };
const EVENT_THRESHOLD = 50000;

// everything the dashboard needs from a plan, computed once per plan
export function planOutlook(plan) {
  if (!plan || !Array.isArray(plan.daily) || !plan.daily.length) return null;
  // never read past the plan's own horizon (a shorter cached plan would otherwise crash the card)
  const horizon = Math.min(DASHBOARD_HORIZON, plan.daily.length - 1);
  const expectedSeries = buildExpectedSeries(plan, DASHBOARD_LAYERS);
  const intercompanySeries = buildIntercompanySeries(plan, DASHBOARD_LAYERS, expectedSeries);
  return {
    horizon,
    expectedSeries,
    chartData: buildBalanceChartData(plan, horizon, expectedSeries, intercompanySeries),
    events: collectChartEvents(plan, { horizon, layers: DASHBOARD_LAYERS, eventThreshold: EVENT_THRESHOLD }),
    summary: summarizeHorizon(plan, { horizon, layers: DASHBOARD_LAYERS, expectedSeries, intercompanySeries })
  };
}

function Stat({ label, value, detail, tone }) {
  return (
    <div style={{ minWidth: 150 }}>
      <Text as="p" variant="bodySm" tone="subdued">
        {label}
      </Text>
      <Text as="p" variant="headingLg" tone={tone}>
        {value}
      </Text>
      {detail ? (
        <Text as="p" variant="bodySm" tone="subdued">
          {detail}
        </Text>
      ) : null}
    </div>
  );
}

export default function CashPlanningCard({ plan, error, onRetry, planningUrl }) {
  const outlook = useMemo(() => planOutlook(plan), [plan]);

  const lastSync = plan?.accounts
    ?.map((account) => account.last_synced_at)
    .filter(Boolean)
    .sort()
    .pop();

  return (
    <Card>
      <InlineStack align="space-between" blockAlign="start" gap="400">
        <div>
          <CardHeading title={`Cash planning — bank balance, last 90 days and next ${outlook?.horizon ?? DASHBOARD_HORIZON} days`} />
          <Text as="p" variant="bodySm" tone="subdued">
            Expected scenario from open invoices, loans, salaries and recurring bank flows; cautious makes customers pay late and leaves doubtful items out. Both leave intercompany documents (other
            group companies) out — the dotted line adds them back. Order backlog and open purchase orders are not included here (switch them on in the cash planning tab).
          </Text>
        </div>
        {planningUrl ? (
          <Link removeUnderline url={planningUrl}>
            Open cash planning
          </Link>
        ) : null}
      </InlineStack>
      {error ? (
        <div style={{ marginTop: 12 }}>
          <Banner tone="critical" title="Cash planning could not be loaded" action={onRetry ? { content: 'Retry', onAction: onRetry } : undefined}>
            <p>{error}</p>
          </Banner>
        </div>
      ) : !plan || !outlook ? (
        <div style={{ marginTop: 16 }}>
          <SkeletonBodyText lines={8} />
        </div>
      ) : (
        <>
          {!plan.history?.migrated || !plan.planning?.migrated ? (
            <div style={{ marginTop: 12 }}>
              <Banner tone="warning" title="Cash planning tables are missing on this database">
                <p>Run sql/create_cash_planning_tables.sql once; until then the projection only sees bank transactions since go-live.</p>
              </Banner>
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', margin: '12px 0 4px' }}>
            <Stat
              label="Balance today"
              value={eur(plan.openingBalance)}
              detail={`${plan.accounts.length} account${plan.accounts.length === 1 ? '' : 's'}${lastSync ? `, synced ${formatShortDate(lastSync)}` : ''}`}
            />
            <Stat
              label={`Expected in ${outlook.horizon} days`}
              value={eur(outlook.summary.end)}
              detail={`cautious: ${eur(outlook.summary.endCautious)} · incl. intercompany: ${eur(outlook.summary.endWithIntercompany)}`}
              tone={outlook.summary.end < 0 ? 'critical' : undefined}
            />
            <Stat
              label="Lowest point"
              value={eur(outlook.summary.lowest.balance)}
              detail={`on ${formatShortDate(outlook.summary.lowest.date)} · cautious ${eur(outlook.summary.lowestCautious.cautious)}`}
              tone={outlook.summary.lowest.balance < 0 ? 'critical' : undefined}
            />
            <Stat label={`Money in, ${outlook.horizon} days`} value={eur(outlook.summary.inflow)} tone="success" />
            <Stat label={`Money out, ${outlook.horizon} days`} value={eur(outlook.summary.outflow)} tone="critical" />
          </div>
          <BalanceChart data={outlook.chartData} events={outlook.events} height={300} />
          <EventChips events={outlook.events} />
        </>
      )}
    </Card>
  );
}
